import ApiClient from 'api-client/ApiClient'
import { VMHAStatusDetails } from 'api-client/model'
import { canManageInfrastructure } from 'app/core/utils/rbac'
import store from 'app/store'
import ActionsSet from 'core/actions/ActionsSet'
import CustomAction from 'core/actions/CustomAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import { cacheActions } from 'core/caching/cacheReducers'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import Bugsnag from 'utils/bugsnag'
import { pathStr } from 'utils/fp'
import { trackEvent } from 'utils/tracking'
import { ClusterHostGroup, CreateClusterHostGroup } from './model'

const { resMgr, haMgr } = ApiClient.getInstance()
const { dispatch } = store

// The backend-filtered `ClusterHostGroups` list only holds clusters the current user's tenant can
// use (shared with all tenants, or dedicated to the active tenant), and Create/Clone VM and k8s
// placement pickers read it. So a cluster is written into that cache only when the active tenant
// can use it; otherwise it only belongs in the all-tenants cache.
const isAccessibleToActiveTenant = (body: { tenants?: string[] }) =>
  !body.tenants?.length || body.tenants.includes(store.getState().session?.activeTenantId)

export const resmgrClusterActions = ActionsSet.make<DataKeys.ClusterHostGroups>({
  uniqueIdentifier: 'name',
  entityName: entityNamesByKey[DataKeys.ClusterHostGroups],
  cacheKey: DataKeys.ClusterHostGroups,
})

export const listClusterHostGroups = resmgrClusterActions.add(
  new ListAction<DataKeys.ClusterHostGroups>(async () => {
    Bugsnag.leaveBreadcrumb('Attempting to get clusters')
    return resMgr.getClusterHostGroups()
  })
    .addDependency(DataKeys.ResmgrHosts)
    .addDependency(DataKeys.HostNetworkConfigs),
)

// PCD-11077: /clusters is now filtered server-side by the caller's tenant/role access, so
// `listClusterHostGroups` above only ever returns what the current user can act on — which is
// exactly right for pickers and other action flows (Create/Clone VM, host assignment, cluster
// creation, etc). Read-only admin screens that need to see every cluster regardless of tenant
// (the Clusters list page, cluster details/overview, GPU hosts, dashboards, cross-cluster
// filters) use this separate action/cache key instead, backed by `?all_tenants=true`.
// `all_tenants` is only requested for canManageInfrastructure() roles (the only roles the backend
// allows to use it; anyone else gets a 403). Other users get the default (tenant-filtered) list.
const resmgrClusterAllTenantsActions = ActionsSet.make<DataKeys.ClusterHostGroupsAllTenants>({
  uniqueIdentifier: 'name',
  entityName: entityNamesByKey[DataKeys.ClusterHostGroupsAllTenants],
  cacheKey: DataKeys.ClusterHostGroupsAllTenants,
})

export const listClusterHostGroupsAllTenants = resmgrClusterAllTenantsActions.add(
  new ListAction<DataKeys.ClusterHostGroupsAllTenants>(async () => {
    Bugsnag.leaveBreadcrumb('Attempting to get clusters (all tenants)')
    return resMgr.getClusterHostGroups({ allTenants: canManageInfrastructure() })
  })
    .addDependency(DataKeys.ResmgrHosts)
    .addDependency(DataKeys.HostNetworkConfigs),
)

export const deleteClusterHostGroup = resmgrClusterActions.add(
  new DeleteAction<DataKeys.ClusterHostGroups, { name: string }>(
    async ({ name }) => {
      Bugsnag.leaveBreadcrumb('Attempting to delete cluster', { name })
      await resMgr.deleteClusterHostGroup({ name })
    },
    // Remove from the all-tenants cache too so the cluster leaves every screen that reads it
    { addCacheKey: DataKeys.ClusterHostGroupsAllTenants },
  ),
)

export const createClusterHostGroup = resmgrClusterActions.add(
  new CustomAction<DataKeys.ClusterHostGroups, { body: CreateClusterHostGroup }>(
    'createClusterHostGroup',
    async ({ body }) => {
      Bugsnag.leaveBreadcrumb('Attempting to create cluster', { body })
      const created: ClusterHostGroup = await resMgr.createClusterHostGroup({ body })
      trackEvent('Create New Cluster', {
        name: pathStr('name', created),
      })
      return created
    },
    (_, { body }) => {
      // Always add to the all-tenants cache; the filtered one only if the active tenant can use it
      if (isAccessibleToActiveTenant(body)) {
        dispatch(
          cacheActions.addItem({
            uniqueIdentifier: 'name',
            cacheKey: DataKeys.ClusterHostGroups,
            params: { name: body.name },
            item: body as unknown as Record<string, unknown>,
          }),
        )
      }
      dispatch(
        cacheActions.addItem({
          uniqueIdentifier: 'name',
          cacheKey: DataKeys.ClusterHostGroupsAllTenants,
          params: { name: body.name },
          item: body as unknown as Record<string, unknown>,
        }),
      )
    },
  ),
)

export const updateClusterHostGroup = resmgrClusterActions.add(
  new CustomAction<DataKeys.ClusterHostGroups, { body: CreateClusterHostGroup }>(
    'updateClusterHostGroup',
    async ({ body }) => {
      Bugsnag.leaveBreadcrumb('Attempting to update cluster', { body })
      const updated: ClusterHostGroup = await resMgr.updateClusterHostGroup({
        name: body.name,
        body,
      })
      trackEvent('Update Cluster', {
        name: pathStr('name', updated),
      })

      return updated
    },
    (_, { body }) => {
      // Always update the all-tenants cache. A cluster the active tenant can no longer use must
      // leave the filtered cache; otherwise it is updated in place.
      dispatch(
        isAccessibleToActiveTenant(body)
          ? cacheActions.updateItem({
              uniqueIdentifier: 'name',
              cacheKey: DataKeys.ClusterHostGroups,
              params: { name: body.name },
              item: body as unknown as Record<string, unknown>,
            })
          : cacheActions.removeItem({
              uniqueIdentifier: 'name',
              cacheKey: DataKeys.ClusterHostGroups,
              params: { name: body.name },
            }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'name',
          cacheKey: DataKeys.ClusterHostGroupsAllTenants,
          params: { name: body.name },
          item: body as unknown as Record<string, unknown>,
        }),
      )
    },
  ),
)

// Cluster VMHA Statuses
export const clusterVMHAStatus = ActionsSet.make<DataKeys.ClusterVMHAStatus>({
  uniqueIdentifier: 'name',
  entityName: entityNamesByKey[DataKeys.ClusterVMHAStatus],
  cacheKey: DataKeys.ClusterVMHAStatus,
})

export const listClusterVMHAStatus = clusterVMHAStatus.add(
  new ListAction<DataKeys.ClusterVMHAStatus, { clusters: ClusterHostGroup[] }>(
    async ({ clusters }): Promise<VMHAStatusDetails[]> => {
      try {
        const clusterNames = clusters.map((cluster) => cluster.name)
        const vmhaStatus = await haMgr.getVhmaForAllClusters(clusterNames)
        return vmhaStatus
      } catch (vmhaError) {
        return []
      }
    },
  ),
)
